(() => {
  'use strict';
  const aomTrack = (n, p) => window.aomTrack?.(n, p);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const container = $('#ao-custom-scrollableContainer');
  if (!container) return;

  const leftBtn  = $('#aom_custom_video_btn_left');
  const rightBtn = $('#aom_custom_video_btn_right');
  const cards    = $$('.ao-custom-review-card', container);
  const mqMobile = window.matchMedia('(max-width: 768px)');

  if (cards.length === 0) {
    if (leftBtn)  leftBtn.style.display  = 'none';
    if (rightBtn) rightBtn.style.display = 'none';
    return;
  }

  // Collect video metadata from each rendered card
  const videoData = cards.map((card) => {
    const vid = card.querySelector('video');
    const src = vid?.querySelector('source')?.getAttribute('src')
             || vid?.querySelector('source[data-src]')?.dataset.src
             || '';
    return {
      url:    src.split('#')[0], // strip the #t= poster-hint fragment
      poster: vid?.getAttribute('poster') || '',
      title:  card.dataset.title || '',
    };
  });

  // ─── Scroll / nav ───────────────────────────────────────────────────────────

  function computeScrollStep() {
    const style = getComputedStyle(container);
    const gap   = parseFloat(style.columnGap || style.gap || '0');
    const w     = cards[0]?.getBoundingClientRect().width || 0;
    return Math.ceil(w + gap);
  }
  let scrollStep = computeScrollStep();

  new ResizeObserver(() => {
    scrollStep = computeScrollStep();
    updateNavButtons();
  }).observe(container);

  function setBtnState(btn, enabled) {
    if (!btn) return;
    btn.disabled = !enabled;
    btn.classList.toggle('disabled', !enabled);
    btn.style.display = (mqMobile.matches || cards.length <= 1) ? 'none' : 'flex';
  }

  function updateNavButtons() {
    const atStart = container.scrollLeft <= 1;
    const atEnd   = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;
    setBtnState(leftBtn,  !atStart);
    setBtnState(rightBtn, !atEnd);
  }

  function throttle(fn, limit = 100) {
    let inThrottle = false, lastArgs = null, lastThis = null;
    return function (...args) {
      lastArgs = args; lastThis = this;
      if (!inThrottle) {
        fn.apply(lastThis, lastArgs);
        inThrottle = true;
        setTimeout(() => {
          inThrottle = false;
          if (lastArgs) { fn.apply(lastThis, lastArgs); lastArgs = lastThis = null; }
        }, limit);
      }
    };
  }

  leftBtn?.addEventListener('click',  () => container.scrollBy({ left: -scrollStep, behavior: 'smooth' }));
  rightBtn?.addEventListener('click', () => container.scrollBy({ left:  scrollStep, behavior: 'smooth' }));
  container.addEventListener('scroll', throttle(updateNavButtons, 100), { passive: true });
  mqMobile.addEventListener?.('change', updateNavButtons);
  updateNavButtons();

  // ─── Carousel a11y & lazy-load ──────────────────────────────────────────────

  cards.forEach((c) => {
    if (!c.hasAttribute('tabindex')) c.tabIndex = 0;
    const v = c.querySelector('video');
    if (v) v.setAttribute('playsinline', '');
  });

  const lazyObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const v = entry.target.querySelector('video');
        const s = v?.querySelector('source[data-src]');
        if (s && !s.getAttribute('src')) { s.src = s.dataset.src; v.load(); }
      });
    },
    { root: container, threshold: 0.1 }
  );
  cards.forEach((c) => lazyObserver.observe(c));

  // ─── Fullscreen Modal ────────────────────────────────────────────────────────

  const modal       = $('#ao-vc-modal');
  if (!modal) return;

  // <dialog> opened via showModal() is placed in the browser's top layer,
  // which renders above every stacking context — including other dialogs.
  // No need to move it to <body>.

  const modalVideo  = modal.querySelector('.ao-vc-modal-video');
  const progressWrap = modal.querySelector('.ao-vc-progress-bars');
  const captionEl   = modal.querySelector('.ao-vc-caption');
  const closeBtn    = modal.querySelector('.ao-vc-close');
  const tapPrev     = modal.querySelector('.ao-vc-tap-prev');
  const tapNext     = modal.querySelector('.ao-vc-tap-next');
  const playOverlay = modal.querySelector('.ao-vc-play-overlay');
  const playPauseBtn = modal.querySelector('.ao-vc-playpause');
  const muteBtn     = modal.querySelector('.ao-vc-mute');
  const seekBar     = modal.querySelector('.ao-vc-seekbar');

  let currentIdx  = 0;
  let rafId       = null;
  let isScrubbing = false;

  // Update the range input value AND its background gradient to show played fill
  function updateSeekBar(pct) {
    seekBar.value = pct * 1000;
    const p = (pct * 100).toFixed(2);
    seekBar.style.background =
      `linear-gradient(to right, #fff ${p}%, rgba(255,255,255,0.35) ${p}%)`;
  }

  // Build one progress-bar segment per video in the carousel
  function buildProgressBars() {
    progressWrap.innerHTML = '';
    videoData.forEach(() => {
      const bar  = document.createElement('div');
      bar.className = 'ao-vc-progress-bar';
      const fill = document.createElement('div');
      fill.className = 'ao-vc-progress-bar-fill';
      bar.appendChild(fill);
      progressWrap.appendChild(bar);
    });
  }

  function setProgressBars(idx, pct) {
    progressWrap.querySelectorAll('.ao-vc-progress-bar-fill').forEach((f, i) => {
      f.style.width = i < idx ? '100%' : i === idx ? `${Math.max(0, Math.min(1, pct)) * 100}%` : '0%';
    });
  }

  // RAF loop — updates story segments and bottom seekbar while playing
  function rafLoop() {
    if (!isScrubbing && !modalVideo.paused && modalVideo.duration > 0) {
      const pct = modalVideo.currentTime / modalVideo.duration;
      setProgressBars(currentIdx, pct);
      updateSeekBar(pct);
    }
    rafId = requestAnimationFrame(rafLoop);
  }

  function setPlayState(playing) {
    playPauseBtn.dataset.playing = playing ? '1' : '0';
    playPauseBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    playOverlay.style.display = playing ? 'none' : 'flex';
  }

  function setMuteState(muted) {
    muteBtn.dataset.muted = muted ? '1' : '0';
    muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  function updateNavArrows(idx) {
    tapPrev.style.display = idx === 0 ? 'none' : 'block';
    tapNext.style.display = 'block'; // always visible; on last item it closes the modal
  }

  function goTo(idx) {
    if (idx < 0 || idx >= videoData.length) return;
    const data = videoData[idx];
    currentIdx  = idx;

    modalVideo.pause();
    modalVideo.removeAttribute('src');
    modalVideo.load(); // release previous resource

    modalVideo.src    = data.url;
    modalVideo.poster = data.poster;
    captionEl.textContent = data.title;

    setProgressBars(idx, 0);
    updateSeekBar(0);
    updateNavArrows(idx);

    modalVideo.play().catch(() => setPlayState(false));
  }

  function openModal(idx) {
    aomTrack('aom_video_open', {
      ui_element: 'video_carousel',
      action_type: 'modal_open',
      video_index: idx,
      video_title: videoData[idx]?.title || '',
    });
    buildProgressBars();
    document.body.style.overflow = 'hidden';
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(rafLoop);
    modal.showModal();
    goTo(idx);
  }

  function closeModal() {
    cancelAnimationFrame(rafId);
    rafId = null;
    modalVideo.pause();
    modalVideo.src = '';
    modal.close();
    document.body.style.overflow = '';
  }

  // <dialog> fires 'cancel' on Escape key before closing itself.
  // Prevent the default close so our cleanup logic in closeModal() runs instead.
  modal.addEventListener('cancel', (e) => {
    e.preventDefault();
    closeModal();
  });

  // ── Video events ─────────────────────────────────────────────────────────────
  modalVideo.addEventListener('play', () => {
    setPlayState(true);
    aomTrack('aom_video_play', {
      ui_element: 'video_carousel',
      action_type: 'play',
      video_index: currentIdx,
      video_title: videoData[currentIdx]?.title || '',
    });
  });
  modalVideo.addEventListener('pause', () => { if (!modalVideo.ended) setPlayState(false); });
  modalVideo.addEventListener('ended', () => {
    setProgressBars(currentIdx, 1);
    updateSeekBar(1);
    if (currentIdx < videoData.length - 1) {
      goTo(currentIdx + 1);
    } else {
      closeModal();
    }
  });

  // ── Controls ─────────────────────────────────────────────────────────────────
  // Story tap zones: tap left third = prev, tap right third = next
  tapPrev.addEventListener('click', () => {
    aomTrack('aom_video_navigate', {
      ui_element: 'video_carousel',
      action_type: 'navigate',
      direction: 'prev',
      video_index: currentIdx - 1,
    });
    goTo(currentIdx - 1);
  });
  tapNext.addEventListener('click', () => {
    if (currentIdx === videoData.length - 1) closeModal();
    else {
      aomTrack('aom_video_navigate', {
        ui_element: 'video_carousel',
        action_type: 'navigate',
        direction: 'next',
        video_index: currentIdx + 1,
      });
      goTo(currentIdx + 1);
    }
  });

  // Tapping the play overlay (centre area) plays the video
  playOverlay.addEventListener('click', (e) => {
    e.stopPropagation();
    modalVideo.play().catch(() => {});
  });

  // Tapping the video itself (centre area) toggles play/pause
  modalVideo.addEventListener('click', (e) => {
    e.stopPropagation();
    modalVideo.paused ? modalVideo.play().catch(() => {}) : modalVideo.pause();
  });

  playPauseBtn.addEventListener('click', () =>
    modalVideo.paused ? modalVideo.play().catch(() => {}) : modalVideo.pause());

  muteBtn.addEventListener('click', () => {
    modalVideo.muted = !modalVideo.muted;
    setMuteState(modalVideo.muted);
  });

  closeBtn.addEventListener('click', closeModal);

  // ── Seekbar scrubbing ─────────────────────────────────────────────────────────
  seekBar.addEventListener('pointerdown', () => { isScrubbing = true; });
  seekBar.addEventListener('input', () => {
    const pct = seekBar.value / 1000;
    if (modalVideo.duration > 0) modalVideo.currentTime = pct * modalVideo.duration;
    setProgressBars(currentIdx, pct);
    updateSeekBar(pct);
  });
  seekBar.addEventListener('pointerup', () => { isScrubbing = false; });

  // ── Keyboard ──────────────────────────────────────────────────────────────────
  // Note: Escape is handled via the dialog 'cancel' event above.
  modal.addEventListener('keydown', (e) => {
    switch (e.key) {
      case 'ArrowLeft':   goTo(currentIdx - 1); break;
      case 'ArrowRight':  goTo(currentIdx + 1); break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        modalVideo.paused ? modalVideo.play().catch(() => {}) : modalVideo.pause();
        break;
    }
  });

  // Tap on dark backdrop closes modal
  modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

  // ── Open modal from carousel ───────────────────────────────────────────────────
  container.addEventListener('click', (e) => {
    const card = e.target.closest('.ao-custom-review-card');
    if (!card || !container.contains(card)) return;
    const idx = parseInt(card.dataset.index ?? '-1', 10);
    if (idx >= 0 && idx < videoData.length) openModal(idx);
  });

  // Keyboard on carousel card also opens modal
  container.addEventListener('keydown', (e) => {
    const card = e.target.closest('.ao-custom-review-card');
    if (!card) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const idx = parseInt(card.dataset.index ?? '-1', 10);
      if (idx >= 0) openModal(idx);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      rightBtn?.click();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      leftBtn?.click();
    }
  });

  // Init mute icon state
  setMuteState(modalVideo.muted);
})();

